import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Empty, ErrorBlock, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import {
  SOURCE_LABEL,
  TYPE_LABEL,
  type Customer,
  type TeamMember,
} from "../team.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 团队与客户
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这两样是分销商的核心资产，网页上排成名册。**
 *    手机上一个人占两行，一屏看六七个；分销商想知道的是
 *    "我这个月发展了几个、都是什么时候来的" —— 那是**扫一列日期**的事。
 *
 * ⚠️ 客户的手机号在**服务端**就打过码了 —— 前端拿到的本来就是打码的，
 *    不是这里遮一下（前端拿到完整号码就已经泄露）。
 */

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);

export function DesktopTeam() {
  const search = useSearch({ from: "/team" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /** 只有经销/分销商有团队和客户；供应商没有这个概念。 */
  const orgs = (identities?.orgs ?? []).filter(
    (o) => o.status === "active" && (o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;
  const [tab, setTab] = useState<"team" | "customers">("team");

  const team = useQuery({
    queryKey: ["team", orgId],
    queryFn: () => orgGet<TeamMember[]>("/team", orgId!),
    enabled: Boolean(orgId) && tab === "team",
  });
  const customers = useQuery({
    queryKey: ["customers", orgId],
    queryFn: () => orgGet<{ items: Customer[] }>("/customers?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "customers",
  });

  if (mePending)
    return (
      <AccountShell title="团队与客户">
        <Spinner compact />
      </AccountShell>
    );
  if (!me || orgs.length === 0)
    return (
      <AccountShell title="团队与客户">
        <Empty title="你还不是经销商或分销商" hint="这是商家功能，联系上级商家或平台开通" compact />
      </AccountShell>
    );

  return (
    <AccountShell title="团队与客户">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === orgId
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex border-b border-border">
        {(
          [
            ["team", "我发展的下级"],
            ["customers", "我的客户"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              "relative px-4 py-2.5 text-[15px] transition-colors",
              tab === k
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
            {tab === k && (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-600" />
            )}
          </button>
        ))}
      </div>

      {tab === "team" && (
        <>
          {/* ⚠️ 说清楚下级也能再往下发展，否则他不知道自己团队会长几层。 */}
          <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
            你发展的下级自己也能再发展下级。
            <strong className="text-foreground">只有最近三级参与分佣</strong>
            ，再往下的成交不算你的。
          </p>

          {team.isPending ? (
            <Spinner compact />
          ) : team.error ? (
            <ErrorBlock error={team.error} onRetry={() => team.refetch()} />
          ) : (team.data?.length ?? 0) === 0 ? (
            <Empty
              title="还没有下级"
              hint="把专属链接发给想开店的人，他注册后可以申请入驻"
              compact
            />
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[minmax(0,1fr)_130px_130px_150px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>名称</span>
                <span>类型</span>
                <span>等级</span>
                <span>加入时间</span>
              </div>
              {team.data!.map((m) => (
                <div
                  key={m.id}
                  className="grid grid-cols-[minmax(0,1fr)_130px_130px_150px] items-center gap-4 border-t border-border px-5 py-3 text-[14px]"
                >
                  <span className="min-w-0 truncate">
                    {m.name}
                    {m.status !== "active" && (
                      <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {m.status === "closed" ? "已清退" : m.status}
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground">{TYPE_LABEL[m.type] ?? m.type}</span>
                  <span className="text-muted-foreground">{m.levelKey || "—"}</span>
                  <span className="text-[13px] text-muted-foreground tabular-nums">
                    {new Date(m.createdAt).toLocaleDateString("zh-CN")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "customers" && (
        <>
          <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
            从你的专属链接注册的用户会永久挂在你名下，他们之后下的每一单都算你的。
          </p>

          {customers.isPending ? (
            <Spinner compact />
          ) : customers.error ? (
            <ErrorBlock error={customers.error} onRetry={() => customers.refetch()} />
          ) : (customers.data?.items.length ?? 0) === 0 ? (
            <Empty title="还没有客户" hint="把专属链接发出去，注册的人就归你了" compact />
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[minmax(0,1fr)_150px_140px_140px_130px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>客户</span>
                <span>手机号</span>
                <span>来源</span>
                <span>归属时间</span>
                <span>归属到期</span>
              </div>
              {customers.data!.items.map((c) => (
                <div
                  key={c.userId}
                  className="grid grid-cols-[minmax(0,1fr)_150px_140px_140px_130px] items-center gap-4 border-t border-border px-5 py-3 text-[14px]"
                >
                  <span className="min-w-0 truncate">{c.nickname || "未设昵称"}</span>
                  <span className="text-muted-foreground tabular-nums">{c.phone}</span>
                  <span className="text-muted-foreground">
                    {SOURCE_LABEL[c.source] ?? c.source}
                  </span>
                  <span className="text-[13px] text-muted-foreground tabular-nums">
                    {new Date(c.boundAt).toLocaleDateString("zh-CN")}
                  </span>
                  {/* 归属可以设过期时间（默认永久），到期后这人就不算他的了。 */}
                  <span className="text-[13px] text-amber-600 tabular-nums">
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("zh-CN") : "永久"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </AccountShell>
  );
}
